import { UUID } from 'node:crypto';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { map, Observable } from 'rxjs';
import { IProductionNote, IProductionNotePostRequest } from './production-note.service';
import { UserService } from './user.service';

export interface IPostStockReorderRequest {
	itemAlias: string;
	sequenceNumber: number;
	batchId: UUID;
	terminalId: UUID;
	title: string;
	reorderPartNumber: string;
	incidentCodeId: UUID;
}

export interface IPutStockReorderRequest {
	title: string;
	reorderPartNumber: string;
	handled: boolean;
	incidentCodeId: UUID;
}

export interface IStockReorder {
	id: UUID;
	itemAlias: string;
	sequenceNumber: number;
	batchId: UUID;
	terminalId: UUID;
	title: string;
	reorderPartNumber: string;
	incidentCodeId: UUID;
	handled: boolean;
}

export interface IStockReorderWithNotePostRequest {
	externalReorder: IPostStockReorderRequest;
	productionNote: IProductionNotePostRequest;
}

export interface IStockReorderWithNoteResponse {
	result?: {
		externalReorder: IStockReorder;
		productionNote: IProductionNote;
	};
}

export interface IStockReorderResponse {
	result: IStockReorder[];
}

@Injectable({
	providedIn: 'root',
})
export class StockReorderService {
	constructor(
		private http: HttpClient,
		private userService: UserService,
	) {}

	createStockReorder(stockReorder: IStockReorderWithNotePostRequest): Observable<IStockReorderWithNoteResponse> {
		const request: IStockReorderWithNotePostRequest = {
			externalReorder: {
				terminalId: stockReorder.externalReorder.terminalId,
				batchId: stockReorder.externalReorder.batchId,
				itemAlias: stockReorder.externalReorder.itemAlias,
				sequenceNumber: stockReorder.externalReorder.sequenceNumber,
				title: stockReorder.externalReorder.title,
				incidentCodeId: stockReorder.externalReorder.incidentCodeId,
				reorderPartNumber: stockReorder.externalReorder.reorderPartNumber,
			},
			productionNote: stockReorder.productionNote,
		};

		return this.http.post<IStockReorderWithNoteResponse>(this.getUrl(), request, {
			headers: this.userService.getHeaders(),
		});
	}

	updateStockReorder(id: UUID, stockReorder: IPutStockReorderRequest): Observable<IStockReorder> {
		return this.http.put<IStockReorder>(this.getUrl(id), stockReorder, {
			headers: this.userService.getHeaders(),
		});
	}

	getAll(): Observable<IStockReorder[]> {
		return this.http.get<IStockReorderResponse>(this.getUrl(), { headers: this.userService.getHeaders() }).pipe(map((c) => c.result));
	}

	deleteById(id: string): Observable<any> {
		return this.http.delete(this.getUrl(id), {
			headers: this.userService.getHeaders(),
		});
	}

	getUrl(id?: string | null) {
		return `${environment.awsEnvUri}/mes/external-reorders${id ? `/${id}` : ''}`;
	}
}
